import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './LineSPF.scss'
import { useDispatch, useSelector } from 'react-redux'
import { setLineSPFData, setMoveCellStrategyMode, setSelectedHandsOnePlayer } from '../../Redux/_actions/viewer'
import { getContrastColor, getPosColor, hexToRgb, rgbaToHex } from '../../helpers/helpers'
import { useActionsColors, useCurrentTheme } from '../../hooks/useCurrentTheme'
import GradientLine from './GradientLine'
import useWindowSizes from '../../hooks/useWindowSizes'
import Bar from './Bar'
import Tooltip from './Tooltip'
import { selectMatrixPreferences, selectPreferences } from '../../Redux/selectors/selectors'
import { debounce } from 'lodash'
import { createSelector } from '@reduxjs/toolkit'
import { useCheckForLimit } from '../../hooks/useCheckForLimit'
import classNames from 'classnames'

const memoSelector = createSelector(
	[
		(state) => state.view.isRequest,
		(state) => state.view.action_struct,
		(state) => state.view.strategyFilters,
		(state) => state.view.moveModeStrategy,
		(state) => state.view.lineSPFData,
	],
	(isRequest, action_struct, strategyFilters, moveModeStrategy, lineSPFData) => ({
		isRequest,
		action_struct,
		strategyFilters,
		moveModeStrategy,
		lineSPFData,
	})
)

function LineSPF({ isSettings = false, mockActionStruct = null, mockStrategyFilters = null }) {
	const {
		isRequest,
		action_struct: structure,
		strategyFilters: filters,
		moveModeStrategy,
		lineSPFData,
	} = useSelector(memoSelector)
	const dispatch = useDispatch()
	const { dataNew } = useSelector(selectPreferences)
	const { threshold, rounding, equityLine } = useSelector(selectMatrixPreferences)
	const isLimitOver = useCheckForLimit('onePlayer')

	const action_struct = isSettings ? mockActionStruct : structure
	const strategyFilters = isSettings ? mockStrategyFilters : filters

	const { selectedMatrixIndex, selectedClusterIndex } = lineSPFData
	const { combos, draws, suits, activeActions } = strategyFilters
	const { isDrawReverseActions } = dataNew.common

	const refLineSPFContainer = useRef()
	const [clusterWidth, setClusterWidth] = useState(null)
	const { windowWidth } = useWindowSizes()

	const getColor = useActionsColors()
	const theme = useCurrentTheme()
	const equityColors = theme.matrix.equity
	const customOutlineColor = theme.matrix.substrate['border color']

	function getGradientColor(equity) {
		const minEquityColor = hexToRgb(equityColors.minEquity)
		const midEquityColor = hexToRgb(equityColors.midEquity)
		const maxEquityColor = hexToRgb(equityColors.maxEquity)

		if (equity <= 50) {
			return getPosColor(minEquityColor, midEquityColor, equity, 50)
		}

		return getPosColor(midEquityColor, maxEquityColor, equity - 50, 50)
	}

	const getAllCards = (structure) => {
		/*
	      --- allCards ---
	      key - name of a card. For example: "AsAh"
	      value - object with "actionsValues" and "matrixIndex"
	      actions is Array. It contains objects with keys "height" and "color". For example:
	      {value: 0, color: '#48c7fd'}
	      */
		const allCards = {}
		/*
	      --- equityClusters ---
	      Use object structure for getting unique values of equity.
	      key - equity
	      value - Array of card names. For example:
	      */
		const equityClusters = {}
		const indexCellsEquities = {}

		structure.forEach((action) => {
			const actionName = action.action

			let color = getColor(actionName, action.raiseIndex, action.isAllIn)

			if (activeActions.length === 0 || activeActions.indexOf(action.id) > -1) {
				action.values.forEach((cell, index) => {
					if (indexCellsEquities[index] === undefined) {
						indexCellsEquities[index] = new Set()
					}
					cell.c.forEach((card) => {
						const holeSuit = card.s[1] + card.s[3]
						const checkCombo = combos && combos.includes(card.com)
						const checkDraw = draws && draws.includes(card.draw)
						const checkSuits = suits && suits.includes(holeSuit)

						if (checkDraw && checkSuits && checkCombo && card.abs > threshold) {
							// const equity = Number(toFixed(card.eq, 2))
							const equity = card.eq.toFixed(2)
							const value = card.val.toFixed(2)

							indexCellsEquities[index].add(Number(equity).toFixed(2))

							if (allCards[card.s] === undefined) {
								if (equityClusters[equity] === undefined) {
									equityClusters[equity] = []
								}

								equityClusters[equity].push(card.s)

								allCards[card.s] = {
									actions: [],
									matrixIndex: index,
								}
							}

							allCards[card.s].actions.push({ value, color })
						}
					})
				})
			}
		})

		return [allCards, equityClusters, indexCellsEquities]
	}

	/* useMemo prevents extra functions callings. Functions were called only, when we get new data  */
	const [allCards, equityClusters, indexCellsEquities] = useMemo(
		() => getAllCards(isLimitOver ? [] : action_struct),
		[action_struct, strategyFilters, strategyFilters, strategyFilters, theme, isLimitOver]
	)

	/*
	  SPF line has to be sorted by ascending order.
	  SortedEquities is just array with sorted equities.
	  These values will be used like keys for rendering equityClusters
	  */
	const sortedEquities = useMemo(() => {
		const sortedEquities = Object.keys(equityClusters).sort((eq1, eq2) => eq1 - eq2)
		dispatch(setLineSPFData({ sortedEquities }))

		return sortedEquities
	}, [equityClusters])

	const minEquity = Number(sortedEquities.slice(0, 1)).toFixed(2)
	const maxEquity = Number(sortedEquities.slice(-1)).toFixed(2)

	const gradientData = useMemo(() => {
		return sortedEquities.map((equity) => {
			return { value: equity, color: getGradientColor(equity) }
		})
	}, [sortedEquities])

	const selectMatrixCells = (indexes) => dispatch(setSelectedHandsOnePlayer(indexes))
	const blockSelectMatrixCells = (value) => dispatch(setMoveCellStrategyMode(value))

	const [hoverClusterNumber, setHoverClusterNumber] = useState(null)

	const mouseOverHandler = useCallback(
		debounce((indexes, equity, cluster) => {
			indexes = Array.from(indexes)

			selectMatrixCells(indexes)
			dispatch(
				setLineSPFData({
					selectedEquity: [equity],
					selectedClusterIndex: [cluster],
				})
			)
		}, 200),
		[]
	)

	const mouseLeaveHandler = () => {
		setHoverClusterNumber(null)
		if (moveModeStrategy === false) {
			selectMatrixCells([])
			dispatch(setLineSPFData({ selectedEquity: [], isHover: false, selectedClusterIndex: [] }))
		}
	}

	const onClickClusterHandler = (event, matrixIndexes, clusterIndex, equity) => {
		if (!isSettings) {
			if (selectedClusterIndex.includes(clusterIndex) && selectedClusterIndex.length === 1) {
				if (moveModeStrategy) {
					blockSelectMatrixCells(false)
				} else {
					blockSelectMatrixCells(true)
				}
			} else {
				matrixIndexes = Array.from(matrixIndexes) // 'Set' to 'Array'

				let indexPosition = -1

				matrixIndexes.forEach((matrixIndex) => {
					indexPosition = selectedMatrixIndex.indexOf(matrixIndex)
				})

				clusterIndex = [clusterIndex]
				equity = [equity]

				selectMatrixCells(matrixIndexes)

				dispatch(
					setLineSPFData({
						selectedEquity: equity,
						selectedClusterIndex: clusterIndex,
						selectedMatrixIndex: matrixIndexes,
						isHover: true,
					})
				)

				blockSelectMatrixCells(true)
			}
		}
	}

	useEffect(
		() => {
			/* Reset LineSPF*/
			const highlightedClusters = []

			selectedMatrixIndex.forEach((index) => {
				const equities = Array.from(indexCellsEquities[index])
				equities.forEach((equity) => {
					if (sortedEquities.indexOf(equity) !== -1) {
						const clusterIndex = sortedEquities.indexOf(equity)
						highlightedClusters.push(clusterIndex)
					}
				})
			})

			dispatch(
				setLineSPFData({
					selectedEquity: [],
					selectedClusterIndex: highlightedClusters,
					isHover: false,
				})
			)
			// }
			setHoverClusterNumber(null)
		},
		// [sortedEquities, activeQuery, currentQuery, indexCellsEquities]
		[indexCellsEquities]
	)

	useEffect(() => {
		setClusterWidth(refLineSPFContainer.current.offsetWidth / sortedEquities.length)
	}, [sortedEquities, windowWidth])

	const isShowTooltip = hoverClusterNumber !== null // && hoverClusterNumber <= gradientData.length
	const isShowGradientLine = sortedEquities.length > 0

	return (
		<div className={classNames(`line-spf${!equityLine && !isSettings ? ' line-spf_invisible' : ''}`)}>
			<div className={`line-spf__container${!equityLine ? ' line-spf__container_invisible' : ''}`}>
				<div
					ref={refLineSPFContainer}
					className={`line-spf__clusters-container`}
					onMouseEnter={() => {
						if (!moveModeStrategy && selectedClusterIndex.length === 0 && !isRequest && !isSettings) {
							dispatch(setLineSPFData({ isHover: true }))
						}
					}}
					onMouseLeave={!isSettings ? () => mouseLeaveHandler() : null}
				>
					{sortedEquities.map((equity, index) => {
						const clusterIndex = index
						const cardsCluster = equityClusters[equity]
						const matrixIndexes = new Set()
						equity = Number(equity).toFixed(2)

						const isUnselectedCluster =
							!selectedClusterIndex.includes(clusterIndex) && selectedClusterIndex.length !== 0

						const isSelectedCluster = selectedClusterIndex.includes(clusterIndex) && selectedClusterIndex.length !== 0

						return (
							<svg
								key={equity}
								version='1.1'
								xmlns='http://www.w3.org/2000/svg'
								width='100%'
								className={`line-spf__cluster${isSelectedCluster ? '  line-spf__cluster_selected' : ''}${
									isUnselectedCluster ? ' line-spf__cluster_unselected' : ''
								}`}
								style={{
									'--custom-outline-color': `${customOutlineColor}`,
								}}
								onMouseEnter={
									!isSettings
										? () => {
												if (moveModeStrategy === false) {
													mouseOverHandler(matrixIndexes, equity, clusterIndex)
												}
												setHoverClusterNumber(clusterIndex)
										  }
										: null
								}
								onMouseLeave={() => {
									mouseOverHandler.cancel()
									// mouseLeaveHandler()
								}}
								onMouseDown={(event) => onClickClusterHandler(event, matrixIndexes, clusterIndex, equity)}
							>
								{cardsCluster.map((cardName) => {
									const card = allCards[cardName]
									matrixIndexes.add(card.matrixIndex)

									return (
										<Bar
											key={cardName}
											height='40'
											actions={card.actions}
											isDrawReverseActions={isDrawReverseActions}
										/>
									)
								})}
							</svg>
						)
					})}
				</div>

				<GradientLine
					className={`line-spf__gradient-line${rounding ? ' line-spf__gradient-line_round' : ''}`}
					width='100%'
					height='10'
					data={gradientData}
					isSettings={isSettings}
					isShowGradientLine={isShowGradientLine}
					minEquity={minEquity}
					maxEquity={maxEquity}
				/>
			</div>

			{isShowTooltip && !isSettings && (
				/*
					20.5 = 55 / 2 - 7
					55 - width of tooltip
					7 - padding of 'line-spf'
				*/
				<Tooltip
					position={[
						hoverClusterNumber * clusterWidth + clusterWidth / 2 - 18.5,
						refLineSPFContainer?.current?.offsetTop + refLineSPFContainer?.current?.clientHeight + 16,
					]}
					fontColor={
						gradientData[hoverClusterNumber] && getContrastColor(rgbaToHex(gradientData[hoverClusterNumber].color))
					}
					backgroundColor={getGradientColor(sortedEquities[hoverClusterNumber])}
					content={Number(sortedEquities[hoverClusterNumber]).toFixed(2)}
				/>
			)}
		</div>
	)
}

export default React.memo(LineSPF)
